import React, { useState, useEffect } from 'react';
import './movimentacao.css';
import { useUnits } from '../../context/unitsContext';

// Resolve o id da unidade a partir de uma cela (mesma lógica usada em
// src/pages/Internos/index.js e src/pages/Internos/internalsByUnit.js)
const resolveCellUnitId = (cell) => (
  cell?.pavilion?.unit?.id
  ?? cell?.pavilion?.unit_id
  ?? cell?.pavilion?.unit
  ?? cell?.unit?.id
  ?? cell?.unit_id
  ?? cell?.unit
  ?? null
);

const CellMovements = ({ internalId }) => {
  const { listMovimentCellInternal, getCellById, summaryListUnit } = useUnits();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  const getCellName = (movement, key) => {
    if (key === 'old') {
      return (
        movement?.old_cell_name ||
        movement?.old_cell_data?.name ||
        movement?.old_cell?.name ||
        'Não Informado'
      );
    }

    return (
      movement?.destination_name ||
      movement?.destination_data?.name ||
      movement?.destination?.name ||
      'Não Informado'
    );
  };

  const extractPavilionName = (cellDetail) => {
    if (!cellDetail) return '';
    return (
      cellDetail?.pavilion?.name ||
      cellDetail?.pavilion_name ||
      ''
    );
  };

  useEffect(() => {
    const fetchMovements = async () => {
      if (!internalId) {
        setMovements([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const movementsRes = await listMovimentCellInternal({ internal: internalId });
        const movementsList = Array.isArray(movementsRes?.results)
          ? movementsRes.results
          : Array.isArray(movementsRes)
            ? movementsRes
            : [];

        // Coleta IDs únicos de celas para buscar pavilhão
        const uniqueCellIds = [
          ...new Set(
            movementsList
              .flatMap((m) => [m.old_cell, m.destination])
              .filter((id) => id !== null && id !== undefined)
          ),
        ];

        // Busca celas e unidades em paralelo (ambas silenciosas: uma falha aqui
        // não deve interromper a exibição da lista de movimentações)
        const [cellDetails, unitsRes] = await Promise.all([
          Promise.all(
            uniqueCellIds.map((id) => getCellById(id, { silent: true }).catch(() => null))
          ),
          movementsList.length > 0 ? summaryListUnit().catch(() => null) : Promise.resolve(null),
        ]);

        const cellMap = {};
        uniqueCellIds.forEach((id, index) => {
          cellMap[id] = cellDetails[index] || null;
        });

        // Normaliza lista de unidades (mesma lógica de internalDetails.js, linhas 872-893)
        const unitsList = Array.isArray(unitsRes?.message)
          ? unitsRes.message
          : Array.isArray(unitsRes?.results)
            ? unitsRes.results
            : Array.isArray(unitsRes)
              ? unitsRes
              : [];
        const unitsMap = {};
        unitsList.forEach((unit) => {
          unitsMap[unit.id] = unit;
        });

        const enrichedMovements = movementsList.map((movement) => {
          // Origem e destino deveriam pertencer à mesma unidade (regra de negócio
          // validada em Transferencia/changeCell.js), mas isso não é garantido por
          // constraint de banco — por isso resolvemos os dois lados de forma
          // independente e preferimos o destino, com fallback para a origem.
          const originUnitId = resolveCellUnitId(cellMap[movement.old_cell]);
          const destinationUnitId = resolveCellUnitId(cellMap[movement.destination]);
          const resolvedUnitId = destinationUnitId ?? originUnitId;
          const unitFromMap = unitsMap[resolvedUnitId];
          const unitDisplay = unitFromMap?.name
            || unitFromMap?.acronym
            || resolvedUnitId
            || 'Não informado';

          return {
            ...movement,
            old_cell_name_display: getCellName(movement, 'old'),
            destination_name_display: getCellName(movement, 'destination'),
            old_cell_pavilion: extractPavilionName(cellMap[movement.old_cell]),
            destination_pavilion: extractPavilionName(cellMap[movement.destination]),
            unit_display: unitDisplay,
          };
        });

        setMovements(enrichedMovements);
      } catch (error) {
        console.error('Erro ao buscar movimentações de cela:', error);
        setMovements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMovements();
  }, [internalId, listMovimentCellInternal, getCellById, summaryListUnit]);
  return (
    <div className="discipline-timeline">
      <h3>Histórico de Movimentação de Cela</h3>
      {loading ? (
        <p>Carregando...</p>
      ) : movements.length > 0 ? (
        <ul>
                        {[...movements].sort((a, b) => new Date(b.date) - new Date(a.date)).map((movement, index) => (<li key={index} className="timeline-item-discipline">
            <div className='timeline-dot-span'>
              <div className="timeline-dot-discipline"></div>
              <span className="timeline-date-discipline">{new Date(movement.date).toLocaleString()}</span>
            </div>
            <div className="timeline-content-discipline">
              <strong>De:</strong> <span>{movement.old_cell_name_display || 'Não Informado'}, {movement.old_cell_pavilion ? `Pavilhão (${movement.old_cell_pavilion})` : 'Pavilhão não informado'}</span>
              <strong>Para:</strong> <span>{movement.destination_name_display || 'Não Informado'}, {movement.destination_pavilion ? `Pavilhão (${movement.destination_pavilion})` : 'Pavilhão não informado'}</span>
              <strong>Unidade:</strong> <span>{movement.unit_display}</span>
              <strong>Motivo:</strong> <p>{movement.reason || 'Sem motivo registrado'}</p>
            </div>
          </li>
          ))}
        </ul>
      ) : (
        <p>Nenhuma movimentação registrada.</p>
      )}
    </div>
  );
};

export default CellMovements;
